import React, { useEffect } from 'react';
import { IonRouterOutlet, IonTabs, IonTabBar, IonTabButton, IonIcon, IonLabel, IonBadge, useIonRouter } from '@ionic/react';
import { Route, Routes, Navigate, useLocation } from 'react-router-dom';

import { useAuth } from '../contexts';
import { useInboxUnreadCount } from '../hooks/useInboxUnreadCount';
import { OWNER_ROLES, MANAGER_ROLES, STAFF_ROLES, CLIENT_ROLES, INBOX_ROLES, type Role } from '@rsmaids/shared/constants/roles';
import { ProtectedRoute } from './ProtectedRoute';
import { BillingProtectedRoute } from './BillingProtectedRoute';
import Inbox from '../pages/Inbox';
import Conversation from '../pages/Conversation';

// Dashboard Components
import DashboardOwners from '../pages/dashboard/DashboardOwners';
import DashboardManager from '../pages/dashboard/DashboardManager';
import DashboardMLS from '../pages/dashboard/DashboardMLS';
import DashboardClient from '../pages/dashboard/DashboardClient';

// Booking Components
import Calendar from '../pages/bookingCalendar/Calendar';
import BookingRoutes from '../pages/bookingSystem/index';
import ContactList from '../pages/bookingSystem/ContactsList';
import Contact from '../pages/bookingSystem/Contact';
import TestPage from '../pages/testpage';

// Other Page Components
import AlertsPage from '../pages/AlertsPage';
import NotificationSettings from '../pages/NotificationSettings';
import AppSettings from '../pages/AppSettings';
import ServiceTypeModal from '../components/ServiceTypeModal';
import BookingsListModal from '../components/BookingsListModal';
import EmployeeManagement from '../pages/EmployeeManagement';
import Manage from '../pages/Manage';
import DashboardFinance from '../pages/dashboard/finance/DashboardFinance';
import DashboardSales from '../pages/dashboard/sales/DashboardSales';
import ProfilePage from '../pages/dashboard/Profile';
import Invoice from '../pages/Invoice';
import InvoiceList from '../pages/InvoiceList';
import EstimateList from '../pages/EstimateList';
import { Security } from '../pages/Security/Security';
import Bookings from '../pages/Bookings';
import StaffJobs from '../pages/StaffJobs';
import StaffTasks from '../pages/StaffTasks';
import Leads from '../pages/Leads';
import MySchedule from '../pages/MySchedule';
import Billing from '../pages/Billing';
import HelcimPaymentsModal from '../components/HelcimPaymentsModal';

import {
  peopleOutline,
  mailOutline,
  business,
  card,
  analytics,
  homeOutline,
  calendarOutline,
  home,
  mail,
  people,
  calendar,
  listOutline,
  list,
  chatbubblesOutline,
  chatbubbles,
  menu,
  menuOutline,
} from 'ionicons/icons';
import { useTranslation } from 'react-i18next';

interface NavItem {
  tab: string;
  href: string;
  icon: string;
  activeIcon: string;
  label: string;
}

export const RoleBasedTabs: React.FC = () => {
  const { t: tr } = useTranslation();
  const { authState } = useAuth();
  const { user, isLoading } = authState;
  const currentRole = user?.role as Role | undefined;
  const location = useLocation();
  const ionRouter = useIonRouter();
  // Unread messages from every source (chat, texts, email) — the Inbox tab's badge
  // Only the roles that may use the Inbox listen to it (the chat database refuses everyone else)
  const inboxUnread = useInboxUnreadCount(!!user && INBOX_ROLES.includes(user.role as Role));

  // SoftPhoneModal is mounted as a sibling of IonReactRouter (see App.tsx), so it has no
  // router context of its own and can't call ionRouter.push directly. "View Contact" from a
  // conversation embedded in the dialer dispatches this event instead; we're inside the
  // router tree here, so we can turn it into a real navigation and close the dialer.
  useEffect(() => {
    const handler = (e: Event) => {
      const contactId = (e as CustomEvent<{ contactId?: string }>).detail?.contactId;
      if (!contactId) return;
      window.dispatchEvent(new CustomEvent('rs:closeDialer'));
      ionRouter.push(`/tabs/contact/${contactId}`, 'forward', 'push');
    };
    window.addEventListener('rs:viewContact', handler);
    return () => window.removeEventListener('rs:viewContact', handler);
  }, [ionRouter]);

  // Wait for auth state to hydrate
  if (isLoading) {
    return null;
  }

  if (!user || !currentRole) {
    return <Navigate to="/login" replace />;
  }

  const hideTabs =
    location.pathname === '/tabs/profile' ||
    location.pathname.startsWith('/tabs/contact/edit/') ||
    location.pathname.startsWith('/tabs/booking/') ||
    location.pathname.startsWith('/tabs/invoice/') ||
    location.pathname.startsWith('/tabs/estimate/') ||
    location.pathname === '/tabs/invoices' ||
    location.pathname === '/tabs/estimates' ||
    location.pathname === '/tabs/employeeManagement' ||
    location.pathname === '/tabs/security' ||
    location.pathname === '/tabs/billing' ||
    location.pathname === '/tabs/payments' ||
    location.pathname.startsWith('/tabs/inbox/');

  const getVisibleTabs = (): NavItem[] => {
    switch (currentRole) {
      case 'admin':
      case 'salesManager':
      case 'financeManager':
      case 'manager':
      case 'lead':
      case 'owner':
      case 'staff':
      case 'client': {
        const tabs: NavItem[] = [
          { tab: 'dashboard', href: '/tabs/dashboard', icon: homeOutline, activeIcon: home, label: tr('routing.RoleBasedTabs.home') },
          { tab: 'bookings', href: '/tabs/bookings', icon: calendarOutline, activeIcon: calendar, label: tr('routing.RoleBasedTabs.bookings') },
          { tab: 'inbox', href: '/tabs/inbox', icon: mailOutline, activeIcon: mail, label: tr('routing.RoleBasedTabs.inbox') },
          { tab: 'manage', href: '/tabs/manage', icon: menuOutline, activeIcon: menu, label: tr('routing.RoleBasedTabs.manage') },
        ];
        if (currentRole === 'admin') {
          tabs.splice(2, 0, { tab: 'contacts', href: '/tabs/contacts', icon: peopleOutline, activeIcon: people, label: tr('routing.RoleBasedTabs.contacts') });
        }
        if (currentRole === 'owner') {
          tabs.splice(2, 0, { tab: 'contacts', href: '/tabs/contacts', icon: peopleOutline, activeIcon: people, label: tr('routing.RoleBasedTabs.contacts') });
        }
        if (currentRole === 'manager') {
          tabs.splice(2, 0, { tab: 'contacts', href: '/tabs/contacts', icon: peopleOutline, activeIcon: people, label: tr('routing.RoleBasedTabs.contacts') });
        }
        if (currentRole === 'salesManager') {
          tabs.splice(2, 0, { tab: 'reports', href: '/tabs/reports', icon: analytics, activeIcon: analytics, label: tr('routing.RoleBasedTabs.reports') });
        }
        if (currentRole === 'financeManager') {
          tabs.splice(2, 0, { tab: 'billing', href: '/tabs/billing', icon: card, activeIcon: card, label: tr('routing.RoleBasedTabs.billing') });
          tabs.splice(3, 0, { tab: 'reports', href: '/tabs/reports', icon: analytics, activeIcon: analytics, label: tr('routing.RoleBasedTabs.reports') });
        }
        if (currentRole === 'lead' || currentRole === 'staff') {
          const bookingsIdx = tabs.findIndex(t => t.tab === 'bookings');
          if (bookingsIdx !== -1) {
            tabs[bookingsIdx] = { tab: 'jobs', href: '/tabs/jobs', icon: listOutline, activeIcon: list, label: 'Jobs' };
          }
          const inboxIdx = tabs.findIndex(t => t.tab === 'inbox');
          if (inboxIdx !== -1) {
            tabs[inboxIdx] = { tab: 'chat', href: '/team/messages', icon: chatbubblesOutline, activeIcon: chatbubbles, label: 'Chat' };
          }
          tabs.splice(2, 0, { tab: 'tasks', href: '/tabs/tasks', icon: business, activeIcon: business, label: tr('routing.RoleBasedTabs.tasks') });
        }
        if (currentRole === 'client') {
          tabs.splice(2, 0, { tab: 'services', href: '/tabs/services', icon: business, activeIcon: business, label: tr('routing.RoleBasedTabs.services') });
        }
        return tabs;
      }
      default:
        return [];
    }
  };

  const visibleTabs = getVisibleTabs();

  return (
    <IonTabs>
      <IonRouterOutlet>
      <Routes>
        <Route path="/tabs/dashboard" element={(() => {
          switch (currentRole) {
            case 'admin':
              return <ProtectedRoute allowedRoles={[...OWNER_ROLES]}><DashboardOwners /></ProtectedRoute>;
            case 'owner':
              return <ProtectedRoute allowedRoles={[...OWNER_ROLES]}><DashboardOwners /></ProtectedRoute>;
            case 'manager':
              return <ProtectedRoute allowedRoles={[...MANAGER_ROLES]}><DashboardManager /></ProtectedRoute>;
            case 'salesManager':
              return <ProtectedRoute allowedRoles={[...MANAGER_ROLES]}><DashboardSales /></ProtectedRoute>;
            case 'financeManager':
              return <ProtectedRoute allowedRoles={[...MANAGER_ROLES]}><DashboardFinance /></ProtectedRoute>;
            case 'lead':
            case 'staff':
              return <ProtectedRoute allowedRoles={[...STAFF_ROLES]}><DashboardMLS /></ProtectedRoute>;
            case 'client':
              return <ProtectedRoute allowedRoles={[...CLIENT_ROLES]}><DashboardClient /></ProtectedRoute>;
            default:
              return <Navigate to="/login" replace />;
          }
        })()} />

        <Route path="/tabs/analytics" element={<ProtectedRoute allowedRoles={[...OWNER_ROLES, ...MANAGER_ROLES]}><DashboardSales /></ProtectedRoute>} />
        <Route path="/tabs/testpage" element={<ProtectedRoute allowedRoles={[...OWNER_ROLES, ...MANAGER_ROLES, ...STAFF_ROLES, ...CLIENT_ROLES]}><TestPage /></ProtectedRoute>} />
        <Route path="/tabs/billing" element={<BillingProtectedRoute><Billing /></BillingProtectedRoute>} />
        <Route path="/tabs/payments" element={
          <BillingProtectedRoute>
            <HelcimPaymentsModal
              isOpen={true}
              asPage={true}
              onClose={() => {
                if (ionRouter.canGoBack()) {
                  ionRouter.goBack();
                  return;
                }
                ionRouter.push('/tabs/dashboard', 'root', 'replace');
              }}
            />
          </BillingProtectedRoute>
        } />
        <Route path="/tabs/staff" element={<ProtectedRoute allowedRoles={[...OWNER_ROLES, ...MANAGER_ROLES]}><EmployeeManagement /></ProtectedRoute>} />
        <Route path="/tabs/reports" element={<ProtectedRoute allowedRoles={[...OWNER_ROLES, ...MANAGER_ROLES]}><DashboardSales /></ProtectedRoute>} />
        <Route path="/tabs/tasks" element={<ProtectedRoute allowedRoles={[...STAFF_ROLES]}><StaffTasks /></ProtectedRoute>} />
        <Route path="/tabs/services" element={<ProtectedRoute allowedRoles={[...CLIENT_ROLES]}><BookingRoutes /></ProtectedRoute>} />
        <Route path="/tabs/service-types" element={<ProtectedRoute allowedRoles={[...OWNER_ROLES, ...MANAGER_ROLES]}><ServiceTypeModal asPage /></ProtectedRoute>} />
        <Route path="/tabs/inbox/:id" element={<ProtectedRoute allowedRoles={[...OWNER_ROLES, ...MANAGER_ROLES, ...STAFF_ROLES, ...CLIENT_ROLES]}><Conversation inboxRoute="/tabs/inbox" /></ProtectedRoute>} />
        <Route path="/tabs/inbox" element={<ProtectedRoute allowedRoles={[...OWNER_ROLES, ...MANAGER_ROLES, ...STAFF_ROLES, ...CLIENT_ROLES]}><Inbox /></ProtectedRoute>} />
        <Route path="/tabs/calendar" element={<ProtectedRoute allowedRoles={[...OWNER_ROLES, ...MANAGER_ROLES, ...STAFF_ROLES, ...CLIENT_ROLES]}><Calendar /></ProtectedRoute>} />
        <Route path="/tabs/bookings" element={<ProtectedRoute allowedRoles={[...OWNER_ROLES, ...MANAGER_ROLES]}><Bookings /></ProtectedRoute>} />
        <Route path="/tabs/bookings-list" element={<ProtectedRoute allowedRoles={[...OWNER_ROLES, ...MANAGER_ROLES]}><BookingsListModal asPage /></ProtectedRoute>} />
        <Route path="/tabs/jobs" element={<ProtectedRoute allowedRoles={[...STAFF_ROLES]}><StaffJobs /></ProtectedRoute>} />
        <Route path="/tabs/invoice/:invoiceId" element={<ProtectedRoute allowedRoles={[...OWNER_ROLES, ...MANAGER_ROLES]}><Invoice /></ProtectedRoute>} />
        <Route path="/tabs/estimate/:estimateId" element={<ProtectedRoute allowedRoles={[...OWNER_ROLES, ...MANAGER_ROLES]}><Invoice documentType="estimate" /></ProtectedRoute>} />
        <Route path="/tabs/invoices" element={<ProtectedRoute allowedRoles={[...OWNER_ROLES, ...MANAGER_ROLES]}><InvoiceList /></ProtectedRoute>} />
        <Route path="/tabs/estimates" element={<ProtectedRoute allowedRoles={[...OWNER_ROLES, ...MANAGER_ROLES]}><EstimateList /></ProtectedRoute>} />
        <Route path="/tabs/leads" element={<ProtectedRoute allowedRoles={[...OWNER_ROLES, ...MANAGER_ROLES]}><Leads /></ProtectedRoute>} />
        <Route path="/tabs/contacts" element={<ProtectedRoute allowedRoles={[...OWNER_ROLES, ...MANAGER_ROLES]}><ContactList /></ProtectedRoute>} />
        <Route path="/tabs/contact/:id" element={<ProtectedRoute allowedRoles={[...OWNER_ROLES, ...MANAGER_ROLES]}><Contact /></ProtectedRoute>} />
        <Route path="/tabs/manage" element={<ProtectedRoute allowedRoles={[...OWNER_ROLES, ...MANAGER_ROLES, ...STAFF_ROLES, ...CLIENT_ROLES]}><Manage /></ProtectedRoute>} />
        <Route path="/tabs/security" element={<ProtectedRoute allowedRoles={[...OWNER_ROLES, ...MANAGER_ROLES, ...STAFF_ROLES, ...CLIENT_ROLES]}><Security /></ProtectedRoute>} />
        <Route path="/tabs/employeeManagement" element={<ProtectedRoute allowedRoles={[...OWNER_ROLES, ...MANAGER_ROLES]}><EmployeeManagement /></ProtectedRoute>} />
        <Route path="/tabs/profile" element={<ProtectedRoute allowedRoles={[...OWNER_ROLES, ...MANAGER_ROLES, ...STAFF_ROLES, ...CLIENT_ROLES]}><ProfilePage /></ProtectedRoute>} />
        <Route path="/tabs/dashboard/owner/alerts" element={<ProtectedRoute allowedRoles={[...OWNER_ROLES]}><AlertsPage /></ProtectedRoute>} />
        <Route path="/tabs/dashboard/sales/alerts" element={<ProtectedRoute allowedRoles={[...MANAGER_ROLES]}><AlertsPage /></ProtectedRoute>} />
        <Route path="/tabs/dashboard/manager/alerts" element={<ProtectedRoute allowedRoles={[...MANAGER_ROLES]}><AlertsPage /></ProtectedRoute>} />
        <Route path="/tabs/dashboard/finance/alerts" element={<ProtectedRoute allowedRoles={[...MANAGER_ROLES]}><AlertsPage /></ProtectedRoute>} />
        <Route path="/tabs/dashboard/mls/alerts" element={<ProtectedRoute allowedRoles={[...STAFF_ROLES]}><AlertsPage /></ProtectedRoute>} />
        <Route path="/tabs/dashboard/client/alerts" element={<ProtectedRoute allowedRoles={[...CLIENT_ROLES]}><AlertsPage /></ProtectedRoute>} />
        <Route path="/tabs/alerts" element={<ProtectedRoute allowedRoles={[...OWNER_ROLES, ...MANAGER_ROLES, ...STAFF_ROLES, ...CLIENT_ROLES]}><AlertsPage /></ProtectedRoute>} />
        <Route path="/tabs/dashboard/owner/notifications" element={<ProtectedRoute allowedRoles={[...OWNER_ROLES]}><NotificationSettings /></ProtectedRoute>} />
        <Route path="/tabs/notifications" element={<ProtectedRoute allowedRoles={[...OWNER_ROLES, ...MANAGER_ROLES, ...STAFF_ROLES, ...CLIENT_ROLES]}><NotificationSettings /></ProtectedRoute>} />
        <Route path="/tabs/dashboard/owner/settings" element={<ProtectedRoute allowedRoles={[...OWNER_ROLES]}><AppSettings /></ProtectedRoute>} />
        <Route path="/tabs/settings" element={<ProtectedRoute allowedRoles={[...OWNER_ROLES, ...MANAGER_ROLES, ...STAFF_ROLES, ...CLIENT_ROLES]}><AppSettings /></ProtectedRoute>} />
        <Route path="/tabs/my-schedule" element={<ProtectedRoute allowedRoles={[...OWNER_ROLES, ...MANAGER_ROLES, ...STAFF_ROLES]}><MySchedule /></ProtectedRoute>} />
      </Routes>
      </IonRouterOutlet>

      {!hideTabs && (
        <IonTabBar slot="bottom">
          {visibleTabs.map((tab: NavItem) => (
            <IonTabButton key={tab.tab} tab={tab.tab} href={tab.href}>
              <IonIcon icon={tab.icon} className="icon-default" />
              <IonIcon icon={tab.activeIcon} className="icon-active" />
              <IonLabel>{tab.label}</IonLabel>
              {tab.tab === 'inbox' && inboxUnread > 0 && (
                <IonBadge color="danger">{inboxUnread > 99 ? '99+' : inboxUnread}</IonBadge>
              )}
            </IonTabButton>
          ))}
        </IonTabBar>
      )}
    </IonTabs>
  );
};

export default RoleBasedTabs;
